import axios from "axios";
import { AuthContext } from "../../../context/AuthContext";
import { useState, useEffect, useContext, useMemo } from "react";
import { Table, Button, Typography, Select, Row, Col, Input } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import dayjs from "dayjs";

const { Title } = Typography;

export default function CompoffReport() {
    const { authData, backUrl } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [compoffData, setCompoffData] = useState([]);

    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    const [selectedOrg, setSelectedOrg] = useState(null);
    const [selectedDept, setSelectedDept] = useState(null);
    const [selectedDesig, setSelectedDesig] = useState(null);

    const [searchText, setSearchText] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    /* ---------------- FETCH MASTER DATA & COMPOFF ---------------- */

    useEffect(() => {
        fetchOrganisations();
        fetchCompoffRecords();
    }, []);

    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            setOrganisations(res.data.results || []);
        } catch (error) {
            console.error("Error fetching organisations:", error);
        }
    };

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDepartments(res.data.results || []);
        } catch (error) {
            console.error("Error fetching departments:", error);
            setDepartments([]);
        }
    };

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDesignations(res.data.results || []);
        } catch (error) {
            console.error("Error fetching designations:", error);
            setDesignations([]);
        }
    };

    const fetchCompoffRecords = async () => {
        try {
            setLoading(true);
            // Adjust endpoint if needed; assuming it returns availed comp offs
            const res = await axios.get(
                `${backUrl}/api/hr/compoffs/`, // or /my-compoffs/ if user-specific
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setCompoffData(res.data.results || []);
        } catch (error) {
            console.error("Error fetching compoff records:", error);
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- FILTER HANDLERS ---------------- */

    const handleOrgChange = (value) => {
        setSelectedOrg(value);
        setSelectedDept(null);
        setSelectedDesig(null);
        setDepartments([]);
        setDesignations([]);
        setCurrentPage(1);
        if (value) fetchDepartments(value);
    };

    const handleDeptChange = (value) => {
        setSelectedDept(value);
        setSelectedDesig(null);
        setDesignations([]);
        setCurrentPage(1);
        if (value) fetchDesignations(value);
    };

    const handleDesigChange = (value) => {
        setSelectedDesig(value);
        setCurrentPage(1);
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
        setCurrentPage(1);
    };

    /* ---------------- TABLE DATA MAPPING ---------------- */

    const tableData = useMemo(() => {
        return compoffData.map(item => {
            // Get the last approver (final approval)
            const approvals = item.approvals || [];
            const lastApproval = approvals[approvals.length - 1];
            const approvedBy = lastApproval?.approver?.first_name
                ? `${lastApproval.approver.first_name} ${lastApproval.approver.last_name || ''}`.trim()
                : "-";

            // Month-Year based on availed date (or work_date if preferred)
            const availedDate = item.availed_date || item.work_date; // fallback if no availed_date
            const monthYear = dayjs(availedDate).format("MMMM-YYYY");

            return {
                key: item.id,

                organisationId: item.employee?.organisation,
                departmentId: item.employee?.department,
                designationId: item.employee?.designation,

                monthYear,
                empId: item.employee?.user?.username || "-",
                empName: `${item.employee?.user?.first_name || ""} ${item.employee?.user?.last_name || ""}`.trim() || "-",
                designationName: item.employee?.designation_name || "-",
                departmentName: item.employee?.department_name || "-",
                site: item.employee?.location || "-",

                workedDate: item.work_date ? dayjs(item.work_date).format("DD-MM-YYYY") : "-",
                compOffAvailedDate: item.availed_date ? dayjs(item.availed_date).format("DD-MM-YYYY") : "-", // assuming availed_date exists for Availed status
                totalCompOffDays: item.days_available || 1, // usually 1 day per comp off
                reason: item.reason || "-",
                approvedBy,
                remarks: item.cancellation_reason || item.remarks || "-", // fallback to cancellation_reason or any remarks field
            };
        });
    }, [compoffData]);

    /* ---------------- APPLY FILTERS ---------------- */

    const filteredTableData = useMemo(() => {
        return tableData.filter(row => {
            if (selectedOrg && row.organisationId !== selectedOrg) return false;
            if (selectedDept && row.departmentId !== selectedDept) return false;
            if (selectedDesig && row.designationId !== selectedDesig) return false;

            if (searchText.trim()) {
                const searchLower = searchText.toLowerCase().trim();
                const empIdMatch = row.empId.toLowerCase().includes(searchLower);
                const empNameMatch = row.empName.toLowerCase().includes(searchLower);
                if (!empIdMatch && !empNameMatch) return false;
            }

            return true;
        });
    }, [tableData, selectedOrg, selectedDept, selectedDesig, searchText]);

    /* ---------------- TABLE COLUMNS ---------------- */

    const columns = [
        {
            title: "S.No",
            render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
            width: 70,
            fixed: "left"
        },
        { title: "Month–Year", dataIndex: "monthYear", width: 120 },
        { title: "Employee ID", dataIndex: "empId", width: 120 },
        { title: "Employee Name", dataIndex: "empName", width: 180 },
        { title: "Designation", dataIndex: "designationName", width: 160 },
        { title: "Department", dataIndex: "departmentName", width: 140 },
        { title: "Site/Location", dataIndex: "site", width: 140 },
        { title: "Worked Date", dataIndex: "workedDate", width: 130 },
        { title: "Comp Off Availed Date", dataIndex: "compOffAvailedDate", width: 160 },
        { title: "Total Comp Off Days", dataIndex: "totalCompOffDays", width: 140 },
        { title: "Reason", dataIndex: "reason", width: 200 },
        { title: "Approved By", dataIndex: "approvedBy", width: 150 },
        { title: "Remarks/Description", dataIndex: "remarks", width: 250 },
    ];

    /* ---------------- EXCEL DOWNLOAD ---------------- */

    const downloadExcel = () => {
        const excelData = filteredTableData.map((row, index) => ({
            "S.No": index + 1,
            "Month–Year": row.monthYear,
            "Employee ID": row.empId,
            "Employee Name": row.empName,
            "Designation": row.designationName,
            "Department": row.departmentName,
            "Site/Location": row.site,
            "Worked Date": row.workedDate,
            "Comp Off Availed Date": row.compOffAvailedDate,
            "Total Comp Off Days": row.totalCompOffDays,
            "Reason": row.reason,
            "Approved By": row.approvedBy,
            "Remarks/Description": row.remarks,
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Compoff Report");

        // Style header row
        const range = XLSX.utils.decode_range(worksheet["!ref"]);
        for (let C = range.s.c; C <= range.e.c; ++C) {
            const address = XLSX.utils.encode_cell({ r: 0, c: C });
            if (!worksheet[address]) continue;
            worksheet[address].s = {
                fill: { fgColor: { rgb: "0e497b" } },
                font: { color: { rgb: "FFFFFF" }, bold: true },
                alignment: { horizontal: "center" }
            };
        }

        const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([buffer]), "Compoff_Report.xlsx");
    };

    return (
        <>
            <Title level={4}>Comp Off Report</Title>

            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={5}>
                    <Select
                        placeholder="Select Organisation"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        value={selectedOrg}
                        onChange={handleOrgChange}
                        style={{ width: "100%" }}
                    >
                        {organisations.map(org => (
                            <Select.Option key={org.id} value={org.id}>
                                {org.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Select
                        placeholder="Select Department"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        disabled={!selectedOrg}
                        value={selectedDept}
                        onChange={handleDeptChange}
                        style={{ width: "100%" }}
                    >
                        {departments.map(dept => (
                            <Select.Option key={dept.id} value={dept.id}>
                                {dept.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Select
                        placeholder="Select Designation"
                        allowClear
                        showSearch
                        optionFilterProp="children"
                        disabled={!selectedDept}
                        value={selectedDesig}
                        onChange={handleDesigChange}
                        style={{ width: "100%" }}
                    >
                        {designations.map(des => (
                            <Select.Option key={des.id} value={des.id}>
                                {des.name}
                            </Select.Option>
                        ))}
                    </Select>
                </Col>

                <Col span={5}>
                    <Input
                        placeholder="Search by Employee ID or Name"
                        allowClear
                        value={searchText}
                        onChange={handleSearchChange}
                        style={{ width: "100%" }}
                    />
                </Col>

                <Col span={4} style={{ textAlign: "right" }}>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={downloadExcel}
                        style={{ backgroundColor: "#0e497b" }}
                    >
                        Download Excel
                    </Button>
                </Col>
            </Row>

            <Table
                columns={columns}
                dataSource={filteredTableData}
                loading={loading}
                bordered
                pagination={{
                    current: currentPage,
                    pageSize,
                    total: filteredTableData.length,
                    onChange: page => setCurrentPage(page),
                    showSizeChanger: false
                }}
                scroll={{ x: "max-content" }}
                components={{
                    header: {
                        cell: props => (
                            <th {...props} style={{ background: "#0e497b", color: "#fff", textAlign: "center" }}>
                                {props.children}
                            </th>
                        )
                    }
                }}
            />
        </>
    );
}